import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button, Card, CardBody, Input } from '../ui';
import { SectionShell } from './SectionShell';
import type { SectionStatus, TenantSectionProps } from './types';

export interface TelegramValue {
  tokenUploaded?: boolean;
  webhookRegistered?: boolean;
}

type State = 'pending' | 'token-uploading' | 'token-uploaded' | 'webhook-registering' | 'done';

const TOKEN_REGEX = /^\d+:[A-Za-z0-9_-]+$/;

export function telegramStatus(value: TelegramValue | undefined): SectionStatus {
  if (value?.webhookRegistered) return 'complete';
  if (value?.tokenUploaded) return 'partial';
  return 'empty';
}

interface Props extends TenantSectionProps<TelegramValue> {
  /** Wizard: avanzar de paso. No guarda nada, solo navega. */
  onContinue?: () => void;
}

/**
 * Telegram — token de BotFather + webhook.
 * Se guarda en dos llamadas (secreto y webhook), así que trae su propio footer.
 */
export function TelegramSection({ mode, tenantId, value, onSaved, onContinue, testId }: Props) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  // El nombre del bot que Telegram confirmó. Sin esto el dueño no tiene forma de
  // saber si pegó el token del bot correcto (GKTIBO-62).
  const [bot, setBot] = useState<{ username?: string; nombre?: string } | null>(null);
  const [state, setState] = useState<State>(
    value?.tokenUploaded ? (value.webhookRegistered ? 'done' : 'token-uploaded') : 'pending',
  );

  const tokenSaved =
    state === 'token-uploaded' || state === 'webhook-registering' || state === 'done';
  // En edición el operador puede reemplazar el token cuando quiera; en el
  // wizard el flujo es lineal (token → webhook → siguiente).
  const canEditToken = mode === 'edit' || state === 'pending';

  async function uploadToken() {
    if (!TOKEN_REGEX.test(token)) {
      setError('Formato inválido. Debe ser <numero>:<string> de BotFather');
      return;
    }
    setError(null);
    setState('token-uploading');
    try {
      const res = await api.tenants.uploadTelegramToken(tenantId, token);
      setBot(res.bot ?? null);
      onSaved?.({ tokenUploaded: true });
      setState('token-uploaded');
    } catch (err) {
      setError((err as Error).message);
      setState('pending');
    }
  }

  async function setupWebhook() {
    setError(null);
    setState('webhook-registering');
    try {
      await api.tenants.setupTelegramWebhook(tenantId);
      onSaved?.({ webhookRegistered: true });
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', tenantId] });
      setState('done');
    } catch (err) {
      setError((err as Error).message);
      setState('token-uploaded');
    }
  }

  const footer = (
    <>
      {(canEditToken || state === 'token-uploading') && (
        <Button
          variant="primary"
          onClick={uploadToken}
          disabled={!token || state === 'token-uploading'}
          loading={state === 'token-uploading'}
        >
          {state === 'token-uploading' ? 'Subiendo...' : 'Subir token'}
        </Button>
      )}
      {bot?.username && (
        <p className="section-hint" data-testid="telegram-bot-confirmado">
          Conectaste <strong>@{bot.username}</strong>
          {bot.nombre ? ` (${bot.nombre})` : ''}. Si no es el bot que querías, subí otro token.
        </p>
      )}
      {(state === 'token-uploaded' || state === 'webhook-registering') && (
        <Button
          variant="primary"
          onClick={setupWebhook}
          loading={state === 'webhook-registering'}
          disabled={state === 'webhook-registering'}
        >
          Conectar webhook
        </Button>
      )}
      {mode === 'wizard' && state === 'done' && (
        <Button variant="primary" onClick={onContinue}>
          Siguiente →
        </Button>
      )}
      {mode === 'wizard' && (state === 'pending' || state === 'done') && (
        <Button variant="outline" className="ml-auto" onClick={onContinue}>
          Omitir Telegram
        </Button>
      )}
    </>
  );

  return (
    <SectionShell
      mode={mode}
      testId={testId}
      title="Telegram"
      description="Pegá el bot token que te dio @BotFather. El webhook lo conectamos nosotros."
      status={telegramStatus({
        tokenUploaded: tokenSaved,
        webhookRegistered: state === 'done',
      })}
      error={error}
      footer={footer}
    >
      <Input
        label="Bot token"
        id="telegram-token"
        type="password"
        placeholder="1234567890:ABCdef..."
        helper={
          tokenSaved && mode === 'edit' ? 'Pegá uno nuevo para reemplazar el guardado.' : undefined
        }
        value={token}
        onChange={(e) => setToken(e.target.value)}
        disabled={!canEditToken}
      />

      <Card variant="default">
        <CardBody
          data-testid="step-2-status"
          className="text-sm text-neutral-600 dark:text-neutral-300 space-y-2"
        >
          <div>{tokenSaved ? '✅ Token guardado' : '⏳ Esperando token'}</div>
          <div>
            {state === 'done'
              ? '✅ Webhook conectado'
              : state === 'webhook-registering'
                ? '⏳ Registrando webhook...'
                : '⏳ Webhook no conectado'}
          </div>
        </CardBody>
      </Card>
    </SectionShell>
  );
}
